//#region types
import type { InvoiceFormTypes } from "models/formTypes";
import type {
  CostForwardingInvoiceFields,
  ForwardableCost,
  ForwardedCostMissingReason,
} from "models/utilTypes/costForwardingTypes";
import type {
  ForwardedCostsDialogFormat,
  ForwardedCostsMarkupForAll,
  ForwardedCostsSelectAllState,
  ForwardedCostsSelection,
  ForwardedCostsSummary,
} from "models/utilTypes/forwardableCostTypes";
import type { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { CurrencyCode } from "models/enums/currency";
//#endregion
import MathHelper from "./mathHelper";

type Product = InvoiceFormTypes.Product;
type RowId = NonNullable<Product["uniqueId"]>;
type DialogValues = InvoiceFormTypes.ForwardedCostsDialog;
type DialogRow = InvoiceFormTypes.ForwardedCostsDialogRow;

export type ForwardedCostsFormValues = Pick<
  InvoiceFormTypes.Create,
  | "customer"
  | "products"
  | "forwardedCosts"
  | "isDimensionsAdvancedConfiguration"
  | "isPeriodisationAdvancedConfiguration"
> &
  Partial<Pick<InvoiceFormTypes.Create, "currency" | "otherSettings">>;

export interface ForwardedCostRowContext {
  /** SEK per unit of the invoice currency, used only for rows added now. */
  exchangeRate: number;
  fallbackVatRate: number;
  formatVatText: (vatRate: number) => string;
  createBaseRow: () => Product;
}

const priceForwardedCost = (netAmountSek: number, markupPercentage: number | null, exchangeRate: number) =>
  MathHelper.roundAmount((netAmountSek / exchangeRate) * (1 + (markupPercentage ?? 0) / 100));

const needsExchangeRate = (currency?: string) => !!currency && currency.toUpperCase() !== CurrencyCode.SEK;

const normalizeMarkupPercentage = (value: unknown): number | null => {
  if (value === undefined || value === null || String(value).trim() === "") return null;
  const markup = Number(value);
  return Number.isFinite(markup) ? markup : null;
};

// A row must never be left without text, and the description is optional when a cost is marked.
const getForwardedCostText = (cost: ForwardableCost) => cost.description?.trim() || cost.sourceDocument;

const getCustomerId = (values: ForwardedCostsFormValues) =>
  values.customer?.id !== undefined ? Number(values.customer.id) : undefined;

// Costs listed for another customer than the invoice's must never be reserved on it, not even in the render
// before the customer switch has removed their rows.
const getCurrentForwardedCosts = (values: ForwardedCostsFormValues) =>
  values.forwardedCosts?.find((forwarded) => forwarded.customerId === getCustomerId(values));

const getLinksWithRows = (values: ForwardedCostsFormValues, forwarded?: InvoiceFormTypes.ForwardedCosts) => {
  const rowIds = new Set((values.products ?? []).map((p) => p.uniqueId));
  // A row the user deleted by hand no longer forwards its cost, so its link is dropped here.
  return (forwarded?.links ?? []).filter((link) => rowIds.has(link.rowUniqueId));
};

const getActiveLinks = (values: ForwardedCostsFormValues) => getLinksWithRows(values, getCurrentForwardedCosts(values));

// What the dialog can tick: a cost still waiting to be booked has no amount to put on a row.
const canBeAdded = (cost: ForwardableCost) => cost.isSelectable && cost.netAmountSek !== undefined;

const getForwardedCostIds = (values: ForwardedCostsFormValues) => getActiveLinks(values).map((link) => link.costId);

const selectForwardedCostVatRate = (products: Product[], forwardedRowIds: Set<RowId>, fallbackVatRate: number) => {
  const rates = products
    .filter((p) => p.uniqueId === undefined || !forwardedRowIds.has(p.uniqueId))
    .map((p) => Number(p.tax));
  return rates.length > 0 && rates.every((rate) => rate === rates[0]) ? rates[0] : fallbackVatRate;
};

type Link = InvoiceFormTypes.ForwardedCostLink;

// Only the names a link recorded are taken, so a file the user uploaded stays.
const removeLinkFiles = <T extends ForwardedCostsFormValues>(values: T, links: Link[]): T => {
  const names = new Set(links.flatMap((link) => link.attachedFileNames ?? []));
  const attachments = values.otherSettings?.attachments;
  if (names.size === 0 || !attachments) return values;
  return {
    ...values,
    otherSettings: { ...values.otherSettings!, attachments: attachments.filter((file) => !names.has(file.fileName)) },
  };
};

const withoutFilesRecord = ({ attachedFileNames, missingDocument, missingReason, ...link }: Link): Link => link;

// Without per-row editing the first row carries the invoice-level dimensions and periodisation,
// so removing it hands them on to the row that becomes first.
const removeRows = (values: ForwardedCostsFormValues, rowIds: Set<RowId>) => {
  const products = values.products ?? [];
  const remaining = products.filter((p) => p.uniqueId === undefined || !rowIds.has(p.uniqueId));
  const originalFirst = products[0];
  if (remaining.length === 0 || remaining[0] === originalFirst) return remaining;

  return [
    {
      ...remaining[0],
      ...(values.isDimensionsAdvancedConfiguration ? {} : { dimensions: originalFirst.dimensions }),
      ...(values.isPeriodisationAdvancedConfiguration ? {} : { periodisation: originalFirst.periodisation }),
    },
    ...remaining.slice(1),
  ];
};

/** Turns the dialog's selection into invoice rows: adds ticked costs, removes unticked ones, re-prices on a new markup. */
const applyForwardedCostSelection = <T extends ForwardedCostsFormValues>(
  values: T,
  costs: ForwardableCost[],
  selection: ForwardedCostsSelection,
  context: ForwardedCostRowContext
): T => {
  const selectedIds = new Set(selection.selectedCostIds);
  const activeLinks = getActiveLinks(values);
  const keptLinks = activeLinks.filter((link) => selectedIds.has(link.costId));
  const untickedLinks = activeLinks.filter((link) => !selectedIds.has(link.costId));
  const untickedRowIds = new Set(untickedLinks.map((l) => l.rowUniqueId));
  const linksLosingFiles = selection.attachDocuments ? untickedLinks : activeLinks;

  const markupFor = (costId: number) => selection.markupByCostId[costId] ?? null;
  // Only a changed markup re-prices, so a price the user edited by hand survives reopening the dialog.
  // A currency change never converts row prices, so a row priced in another currency is left to the user.
  const repriceLinks = keptLinks.filter(
    (link) => link.currency === values.currency && link.markupPercentage !== markupFor(link.costId)
  );
  const linkByRowId = new Map(keptLinks.map((link) => [link.rowUniqueId, link]));
  const repriceRowIds = new Set(repriceLinks.map((link) => link.rowUniqueId));
  const products = removeRows(values, untickedRowIds).map((p) => {
    const link = p.uniqueId !== undefined && repriceRowIds.has(p.uniqueId) ? linkByRowId.get(p.uniqueId) : undefined;
    return link ? { ...p, price: priceForwardedCost(link.netAmountSek, markupFor(link.costId), link.exchangeRate) } : p;
  });
  // A row left unpriced keeps the markup chosen for it too, so the dialog reopens with it.
  const updatedLinks = keptLinks.map((link) => ({
    ...(selection.attachDocuments ? link : withoutFilesRecord(link)),
    markupPercentage: markupFor(link.costId),
  }));

  const vatRate = selectForwardedCostVatRate(products, new Set(linkByRowId.keys()), context.fallbackVatRate);
  const linkedCostIds = new Set(keptLinks.map((link) => link.costId));
  const newLinks: InvoiceFormTypes.ForwardedCostLink[] = [];
  const newRows = costs
    .filter((c) => selectedIds.has(c.id) && !linkedCostIds.has(c.id) && canBeAdded(c))
    .map((c): Product => {
      const row: Product = {
        ...context.createBaseRow(),
        article: { id: 0, value: getForwardedCostText(c) },
        text: getForwardedCostText(c),
        quantity: 1,
        price: priceForwardedCost(c.netAmountSek!, markupFor(c.id), context.exchangeRate),
        tax: vatRate,
        taxText: context.formatVatText(vatRate),
      };
      newLinks.push({
        costId: c.id,
        rowUniqueId: row.uniqueId!,
        netAmountSek: c.netAmountSek!,
        exchangeRate: context.exchangeRate,
        currency: values.currency,
        markupPercentage: markupFor(c.id),
      });
      return row;
    });

  const customerId = getCustomerId(values);
  return {
    ...removeLinkFiles(values, linksLosingFiles),
    products: [...products, ...newRows],
    forwardedCosts: [
      ...(values.forwardedCosts ?? []).filter((forwarded) => forwarded.customerId !== customerId),
      { customerId, links: [...updatedLinks, ...newLinks], attachDocuments: selection.attachDocuments },
    ],
  };
};

/** For an invoice that cannot carry forwarded costs: their rows go, and nothing is left to reserve. */
const removeForwardedCosts = <T extends ForwardedCostsFormValues>(values: T): T => {
  const activeLinks = getActiveLinks(values);
  return {
    ...removeLinkFiles(values, activeLinks),
    products: removeRows(values, new Set(activeLinks.map((link) => link.rowUniqueId))),
    forwardedCosts: undefined,
  };
};

const getOtherCustomersLinksWithRows = (values: ForwardedCostsFormValues) =>
  (values.forwardedCosts ?? [])
    .filter((forwarded) => forwarded.customerId !== getCustomerId(values))
    .map((forwarded) => ({ forwarded, links: getLinksWithRows(values, forwarded) }))
    .filter(({ links }) => links.length > 0);

const hasOtherCustomersForwardedCosts = (values: ForwardedCostsFormValues) =>
  getOtherCustomersLinksWithRows(values).length > 0;

/**
 * After a customer switch: rows forwarded for another customer would bill this one while that customer's costs
 * stayed open, so they go with the files they added, and their costs and markups are kept for when that customer is
 * chosen again. Switching back adds the rows again and their files are fetched anew.
 */
const removeOtherCustomersForwardedCosts = <T extends ForwardedCostsFormValues>(values: T): T => {
  const removals = getOtherCustomersLinksWithRows(values);
  if (removals.length === 0) return values;

  const removedLinks = removals.flatMap(({ links }) => links);
  const removedRowIds = new Set(removedLinks.map((link) => link.rowUniqueId));
  return {
    ...removeLinkFiles(values, removedLinks),
    products: removeRows(values, removedRowIds),
    forwardedCosts: (values.forwardedCosts ?? []).map((forwarded) => {
      const removal = removals.find((r) => r.forwarded === forwarded);
      return removal
        ? {
            customerId: forwarded.customerId,
            links: [],
            attachDocuments: forwarded.attachDocuments,
            removedCosts: removal.links.map(({ costId, markupPercentage }) => ({ costId, markupPercentage })),
          }
        : forwarded;
    }),
  };
};

/** The kept selection for the invoice's customer, without the costs the fresh list no longer lets the dialog tick. */
const getForwardedCostsToRestore = (values: ForwardedCostsFormValues, costs: ForwardableCost[]) => {
  const current = getCurrentForwardedCosts(values);
  if (!current?.removedCosts) return undefined;

  const addable = new Set(costs.filter(canBeAdded).map((c) => c.id));
  const restorable = current.removedCosts.filter((removed) => addable.has(removed.costId));
  const selection: ForwardedCostsSelection = {
    selectedCostIds: restorable.map((removed) => removed.costId),
    markupByCostId: Object.fromEntries(restorable.map((removed) => [removed.costId, removed.markupPercentage])),
    attachDocuments: current.attachDocuments,
  };
  return { selection, droppedCount: current.removedCosts.length - restorable.length };
};

/**
 * Adds the kept costs back the way the dialog's apply adds them, so a row edited by hand comes back unedited.
 * The costs must be the ones listed for the invoice's customer, else the selection stays kept.
 */
const restoreForwardedCosts = <T extends ForwardedCostsFormValues>(
  values: T,
  listedForCustomerId: number,
  costs: ForwardableCost[],
  context: ForwardedCostRowContext
): T => {
  const restore = getCustomerId(values) === listedForCustomerId ? getForwardedCostsToRestore(values, costs) : undefined;
  return restore ? applyForwardedCostSelection(values, costs, restore.selection, context) : values;
};

/** The PDF preview ignores the fields; only saves reserve, and warnings read the ids to check their source documents. */
const withoutCostForwardingFields = <T extends object>(body: T): Omit<T, keyof CostForwardingInvoiceFields> => {
  const { forwardedCostIds, costMarkupPercentage, attachForwardedCostDocuments, ...request } = body as T &
    Partial<CostForwardingInvoiceFields>;
  return request as Omit<T, keyof CostForwardingInvoiceFields>;
};

const getSharedMarkup = (markups: (number | null)[]) =>
  markups.length > 0 && markups.every((markup) => markup === markups[0]) ? markups[0] : null;

const getForwardedCostsSelection = (values: ForwardedCostsFormValues): ForwardedCostsSelection => {
  const links = getActiveLinks(values);
  return {
    selectedCostIds: links.map((link) => link.costId),
    markupByCostId: Object.fromEntries(links.map((link) => [link.costId, link.markupPercentage])),
    attachDocuments: getCurrentForwardedCosts(values)?.attachDocuments ?? false,
  };
};

// The server takes one markup, kept for reference only: the one every forwarded cost shares, else none.
const getCostForwardingRequestFields = (values: ForwardedCostsFormValues): CostForwardingInvoiceFields => {
  const links = getActiveLinks(values);
  return {
    forwardedCostIds: links.map((link) => link.costId),
    costMarkupPercentage: getSharedMarkup(links.map((link) => link.markupPercentage)),
    // The source files are already among the form's attachments; the server would add them a second time.
    attachForwardedCostDocuments: false,
  };
};

export type ForwardedCostLinkKey = Pick<InvoiceFormTypes.ForwardedCostLink, "costId" | "rowUniqueId">;

export interface ForwardedCostAttachments {
  files: UploadedFile[];
  missingDocument?: string;
  missingReason?: ForwardedCostMissingReason;
}

export interface MissingForwardedCostDocument {
  key: string;
  document: string;
  reason: ForwardedCostMissingReason;
}

const getLinksTakingFiles = (values: ForwardedCostsFormValues) =>
  getCurrentForwardedCosts(values)?.attachDocuments ? getActiveLinks(values) : [];

/**
 * The links whose cost's files are still to be fetched: Bifoga is ticked and the link has no record yet, or its fetch
 * attached nothing for a reason other than access, which may have been a timeout a new confirm can get past.
 */
const getLinksToFetch = (values: ForwardedCostsFormValues) =>
  getLinksTakingFiles(values).filter(
    (link) =>
      link.attachedFileNames === undefined ||
      (link.attachedFileNames.length === 0 && link.missingReason === "notAttached")
  );

/** The source documents whose files could not be attached, for the costs still on the invoice. */
const getMissingDocuments = (values: ForwardedCostsFormValues): MissingForwardedCostDocument[] =>
  getActiveLinks(values).flatMap((link) =>
    link.missingDocument
      ? [
          {
            key: `${link.costId}-${link.rowUniqueId}`,
            document: link.missingDocument,
            reason: link.missingReason ?? "notAttached",
          },
        ]
      : []
  );

/**
 * Adds a cost's fetched files after the attachments already on the invoice and records them on its link. A result
 * for a link that no longer takes files (cost removed, Bifoga unticked, customer switched) leaves the values as is.
 */
const addForwardedCostAttachments = <
  T extends ForwardedCostsFormValues & { otherSettings: Pick<InvoiceFormTypes.OtherSettings, "attachments"> },
>(
  values: T,
  customerId: number,
  linkKey: ForwardedCostLinkKey,
  result: ForwardedCostAttachments
): T => {
  const isLink = (link: InvoiceFormTypes.ForwardedCostLink) =>
    link.costId === linkKey.costId && link.rowUniqueId === linkKey.rowUniqueId;
  if (getCustomerId(values) !== customerId || !getLinksToFetch(values).some(isLink)) return values;

  const attachments = values.otherSettings.attachments ?? [];
  const present = new Set(attachments.map((file) => file.fileName));
  const added = result.files.filter((file) => !present.has(file.fileName));
  return {
    ...values,
    otherSettings: { ...values.otherSettings, attachments: [...attachments, ...added] },
    forwardedCosts: values.forwardedCosts!.map((forwarded) =>
      forwarded.customerId === customerId
        ? {
            ...forwarded,
            links: forwarded.links.map((link) =>
              isLink(link)
                ? {
                    ...link,
                    attachedFileNames: added.map((file) => file.fileName),
                    missingDocument: result.missingDocument,
                    missingReason: result.missingReason,
                  }
                : link
            ),
          }
        : forwarded
    ),
  };
};

const hasFilesRecord = (link: Link) => link.attachedFileNames !== undefined || link.missingDocument !== undefined;

/**
 * A row the user deleted by hand takes the files its cost added and its note. The link stays, without its record,
 * and a link without a row is never fetched.
 */
const removeFilesOfDeletedRows = <T extends ForwardedCostsFormValues>(values: T): T => {
  const rowIds = new Set((values.products ?? []).map((p) => p.uniqueId));
  const isOrphaned = (link: Link) => !rowIds.has(link.rowUniqueId) && hasFilesRecord(link);
  const orphaned = (values.forwardedCosts ?? []).flatMap((forwarded) => forwarded.links.filter(isOrphaned));
  if (orphaned.length === 0) return values;

  return {
    ...removeLinkFiles(values, orphaned),
    forwardedCosts: values.forwardedCosts!.map((forwarded) =>
      forwarded.links.some(isOrphaned)
        ? { ...forwarded, links: forwarded.links.map((link) => (isOrphaned(link) ? withoutFilesRecord(link) : link)) }
        : forwarded
    ),
  };
};

const toForwardedCostsDialogValues = (
  costs: ForwardableCost[],
  selection: ForwardedCostsSelection,
  format: ForwardedCostsDialogFormat
): DialogValues => {
  const selectedIds = new Set(selection.selectedCostIds);
  // A cost not on the invoice yet starts at the markup the added ones share, so the footer keeps showing it.
  const sharedMarkup = getSharedMarkup(selection.selectedCostIds.map((id) => selection.markupByCostId[id] ?? null));
  return {
    rows: costs.map((cost) => ({
      costId: cost.id,
      selected: selectedIds.has(cost.id) && cost.isSelectable,
      isSelectable: cost.isSelectable,
      sourceDocument: cost.sourceDocument,
      date: format.formatDate(cost.sourceDate),
      text: getForwardedCostText(cost),
      amount:
        cost.isSelectable && cost.netAmountSek !== undefined
          ? format.formatAmount(cost.netAmountSek)
          : format.awaitingBookingText,
      markupPercentage: cost.isSelectable
        ? ((selectedIds.has(cost.id) ? selection.markupByCostId[cost.id] : sharedMarkup) ?? undefined)
        : undefined,
    })),
    attachDocuments: selection.attachDocuments,
  };
};

const fromForwardedCostsDialogValues = (values: DialogValues): ForwardedCostsSelection => {
  const ticked = values.rows.filter((row) => row.selected && row.isSelectable);
  return {
    selectedCostIds: ticked.map((row) => row.costId),
    markupByCostId: Object.fromEntries(
      ticked.map((row) => [row.costId, normalizeMarkupPercentage(row.markupPercentage)])
    ),
    attachDocuments: !!values.attachDocuments,
  };
};

// A cost still waiting to be booked has no amount to mark up and cannot be ticked, so the footer and
// select-all pass it by.
const getSelectableRows = (rows: DialogRow[]) => rows.filter((row) => row.isSelectable);

const getForwardedCostsMarkupForAll = (rows: DialogRow[]): ForwardedCostsMarkupForAll => {
  const markups = getSelectableRows(rows).map((row) => normalizeMarkupPercentage(row.markupPercentage));
  const isMixed = markups.some((markup) => markup !== markups[0]);
  return { markupPercentage: isMixed ? undefined : (markups[0] ?? undefined), isMixed };
};

// The footer's number input reports a change of its value prop as a change of its own, so a row edited away from the
// others makes it report an empty value; only a value other than the one it shows was typed by the user.
const shouldApplyMarkupForAll = (rows: DialogRow[], value: unknown) =>
  normalizeMarkupPercentage(value) !== (getForwardedCostsMarkupForAll(rows).markupPercentage ?? null);

const setForwardedCostsMarkupForAll = (values: DialogValues, markupPercentage?: number | string): DialogValues => ({
  ...values,
  rows: values.rows.map((row) => (row.isSelectable ? { ...row, markupPercentage } : row)),
});

const getForwardedCostsSelectAllState = (rows: DialogRow[]): ForwardedCostsSelectAllState => {
  const selectable = getSelectableRows(rows);
  return {
    checked: selectable.length > 0 && selectable.every((row) => row.selected),
    disabled: selectable.length === 0,
  };
};

const selectAllForwardedCosts = (values: DialogValues, selected: boolean): DialogValues => ({
  ...values,
  rows: values.rows.map((row) => (row.isSelectable ? { ...row, selected } : row)),
});

/** What the reminder counts: costs that can be added now and are not already on this invoice. */
const summarizeForwardableCosts = (costs: ForwardableCost[], addedCostIds: number[]): ForwardedCostsSummary => {
  const added = new Set(addedCostIds);
  const pending = costs.filter((c) => canBeAdded(c) && !added.has(c.id));
  return {
    count: pending.length,
    totalNetAmountSek: MathHelper.roundAmount(pending.reduce((sum, c) => sum + c.netAmountSek!, 0)),
  };
};

// The ⋯ menu keeps the entry after the reminder is closed, so the count is what still points at unadded costs.
const getForwardedCostsMenuText = (text: string, summary: ForwardedCostsSummary) =>
  summary.count > 0 ? `${text} (${summary.count})` : text;

// Closing the reminder is the only way it goes; it stays closed for that customer while this form is open.
const shouldShowForwardedCostsReminder = (
  summary: ForwardedCostsSummary,
  invoice: { customerId?: number; dismissedCustomerIds: number[] }
) =>
  summary.count > 0 && invoice.customerId !== undefined && !invoice.dismissedCustomerIds.includes(invoice.customerId);

const getCostForwardingOffer = (
  costs: ForwardableCost[],
  values: ForwardedCostsFormValues,
  invoice: { acceptsForwardedCosts: boolean; isReadonlyUser: boolean; dismissedCustomerIds: number[] }
) => {
  const isOffered = invoice.acceptsForwardedCosts && !invoice.isReadonlyUser;
  const summary = summarizeForwardableCosts(costs, getForwardedCostIds(values));
  return {
    canForwardCosts: isOffered && costs.length > 0,
    summary,
    showReminder:
      isOffered &&
      shouldShowForwardedCostsReminder(summary, {
        customerId: getCustomerId(values),
        dismissedCustomerIds: invoice.dismissedCustomerIds,
      }),
  };
};

const ForwardedCostHelper = {
  getForwardedCostText,
  priceForwardedCost,
  needsExchangeRate,
  normalizeMarkupPercentage,
  getForwardedCostIds,
  selectForwardedCostVatRate,
  applyForwardedCostSelection,
  getForwardedCostsSelection,
  getCostForwardingRequestFields,
  summarizeForwardableCosts,
  shouldShowForwardedCostsReminder,
  getForwardedCostsMenuText,
  toForwardedCostsDialogValues,
  fromForwardedCostsDialogValues,
  getForwardedCostsMarkupForAll,
  shouldApplyMarkupForAll,
  setForwardedCostsMarkupForAll,
  getForwardedCostsSelectAllState,
  selectAllForwardedCosts,
  removeForwardedCosts,
  hasOtherCustomersForwardedCosts,
  removeOtherCustomersForwardedCosts,
  getForwardedCostsToRestore,
  restoreForwardedCosts,
  withoutCostForwardingFields,
  getLinksToFetch,
  getMissingDocuments,
  addForwardedCostAttachments,
  removeFilesOfDeletedRows,
  getCostForwardingOffer,
};

export default ForwardedCostHelper;
